/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { SiFacebook, SiInstagram, SiX, SiYoutube, type IconType } from '@icons-pack/react-simple-icons';
import { Link } from '@/components/link';
import { type LinkValue, resolveLinkHref } from '@/lib/link/link-picker';
import type { LinkPageData } from '@/lib/link/link-picker.server';

const PLATFORM_ICONS: Record<string, IconType> = {
    instagram: SiInstagram,
    facebook: SiFacebook,
    youtube: SiYoutube,
    x: SiX,
};

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('footerSocialLink', {
    name: 'Footer Social Link',
    description: 'A single social media link (platform + URL). Used in the footer social region.',
    group: '4_BlackMagic_Footer',
})
@RegionDefinition([])
export class FooterSocialLinkMetadata {
    @AttributeDefinition({
        id: 'platform',
        name: 'Platform',
        type: 'enum',
        required: true,
        values: ['instagram', 'facebook', 'youtube', 'x'],
        defaultValue: 'instagram',
    })
    platform?: string;

    // Uses the shared link picker (not the built-in `type: 'url'` editor) for consistency with the other
    // footer links; social links are normally an external URL, but Product/Category/Page also resolve.
    // Not `required` — Page Designer can't validate a custom object value on Apply. Legacy strings render.
    @AttributeDefinition({
        id: 'url',
        name: 'Link',
        type: 'custom',
        // Inline literal (not the shared const) — the metadata generator only resolves same-file literals.
        editorDefinition: { type: 'breakout.linkPicker' },
        description: 'Usually the social profile URL (https://…). Product/Category/Page links also work.',
    })
    url?: LinkValue;
}
/* v8 ignore stop */

const iconClass = 'inline-flex transition-opacity hover:opacity-70';

export default function FooterSocialLink({
    platform,
    url,
    data,
}: {
    platform?: string;
    url?: LinkValue;
    data?: LinkPageData;
}): ReactElement | null {
    const Icon = platform ? PLATFORM_ICONS[platform] : undefined;
    const { href, external } = resolveLinkHref(url, data);
    if (!Icon || href === '#') return null;
    // External social URLs open in a new tab; internal object links use the site-aware Link.
    return external ? (
        <a href={href} aria-label={platform} className={iconClass} target="_blank" rel="noopener noreferrer">
            <Icon className="h-5 w-5" />
        </a>
    ) : (
        <Link to={href} aria-label={platform} className={iconClass}>
            <Icon className="h-5 w-5" />
        </Link>
    );
}

// eslint-disable-next-line react-refresh/only-export-components -- loader re-export required for the static-registry AST plugin
export { loader } from './footer-social-link.loaders';
